import {
  AlertTriangle,
  Boxes,
  Cloud,
  Database,
  GitBranch,
  HardDrive,
  Network,
  Server,
  Target,
} from "lucide-react";
import { useScenarioAlertsPreview } from "@/api/scenarios";
import type { ScenarioDetail } from "@/api/types";
import { cn } from "@/lib/utils";

interface Props {
  detail: ScenarioDetail;
}

type Spec = Record<string, unknown>;
type ServiceLike = {
  name?: string;
  team?: string;
  language?: string;
  type?: string;
  depends_on?: string[];
};
type TimelineLike = {
  at?: string;
  event?: string;
  description?: string;
  service?: string;
  caused_by?: string | null;
};

function asObject(v: unknown): Spec | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Spec) : null;
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/**
 * Pick a Lucide icon for a service based on its type (preferred) or language.
 * Falls back to a generic Server glyph so the grid never has missing slots.
 */
function serviceIcon(s: ServiceLike) {
  const lang = (s.language ?? "").toLowerCase();
  const type = (s.type ?? "").toLowerCase();
  if (type === "database" || lang === "postgres" || lang === "mysql" || lang === "mongodb") {
    return Database;
  }
  if (type === "cache" || lang === "redis" || lang === "memcached") return HardDrive;
  if (type === "queue" || lang === "kafka" || lang === "rabbitmq") return GitBranch;
  if (type === "gateway") return Network;
  if (lang === "k8s" || lang === "kubernetes" || lang === "cloud") return Cloud;
  if (type === "worker") return Boxes;
  return Server;
}

/**
 * Style the small event marker — terminal-style anomaly events get a brand
 * accent, deployments get a neutral fill, everything else is hollow.
 */
function eventTone(t: TimelineLike): "anomaly" | "deploy" | "info" {
  const event = (t.event ?? "").toLowerCase();
  if (event.includes("alert") || event.includes("fail") || event.includes("oom")) return "anomaly";
  if (event.includes("deploy") || event.includes("release")) return "deploy";
  return "info";
}

export function ScenarioOverview({ detail }: Props) {
  const spec = (detail.spec ?? {}) as Spec;
  const scenario = asObject(spec.scenario) ?? {};
  const topology = asObject(spec.topology) ?? {};
  const services = asArray<ServiceLike>(topology.services);
  const timeline = asArray<TimelineLike>(spec.timeline);

  return (
    <div className="space-y-6">
      <RootCauseCard rootCause={scenario.root_cause as string | undefined} />

      <PrimaryAlertCard slug={detail.slug} />

      {services.length > 0 && <TopologySection services={services} />}

      {timeline.length > 0 && <TimelineSection events={timeline} />}
    </div>
  );
}

function PrimaryAlertCard({ slug }: { slug: string }) {
  const alerts = useScenarioAlertsPreview(slug);
  if (!alerts.data) return null;
  const primary = alerts.data.find((a) => a.primary);
  const total = alerts.data.length;
  if (!primary) {
    return (
      <section className="rounded-lg border border-dashed border-border bg-muted/30 p-4 flex gap-3">
        <Target size={16} className="mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden />
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            Primary alert
          </h3>
          <p className="text-xs text-muted-foreground">
            No primary alert defined. {total > 0 ? `${total} alerts will fire` : "This scenario fires no alerts"}.
            Mark one timeline event with{" "}
            <code className="font-mono text-foreground">primary: true</code> so an SE knows which alert to
            pick if Falcon's grouping misses some.
          </p>
        </div>
      </section>
    );
  }
  return (
    <section className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 flex gap-3">
      <Target size={16} className="mt-0.5 text-primary flex-shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap mb-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-primary">
            Primary alert
          </h3>
          <span className="text-[11px] text-muted-foreground">
            pick this one to launch the investigation
          </span>
          <span className="ml-auto text-[11px] text-muted-foreground font-mono">
            1 of {total}
          </span>
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground">{primary.title}</span>
        </div>
        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
          {primary.alertname} · {primary.service} · {primary.event_at}
        </div>
        {primary.primary_rationale && (
          <p className="text-xs text-muted-foreground mt-2">{primary.primary_rationale}</p>
        )}
      </div>
    </section>
  );
}

function RootCauseCard({ rootCause }: { rootCause?: string }) {
  if (!rootCause) return null;
  return (
    <section className="rounded-lg border border-border bg-card p-4 flex gap-3">
      <AlertTriangle
        size={16}
        className="mt-0.5 text-amber-500 flex-shrink-0"
        aria-hidden
      />
      <div className="min-w-0">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
          Root cause
        </h3>
        <p className="text-sm text-foreground">{rootCause}</p>
      </div>
    </section>
  );
}

function TopologySection({ services }: { services: ServiceLike[] }) {
  return (
    <section>
      <SectionHeader title="Topology" count={services.length} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {services.map((s, i) => {
          const Icon = serviceIcon(s);
          return (
            <div
              key={(s.name ?? "") + i}
              className="rounded-md border border-border bg-card px-3 py-2 flex items-start gap-2.5"
            >
              <span className="mt-0.5 text-muted-foreground" aria-hidden>
                <Icon size={14} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-mono text-foreground truncate">
                  {s.name ?? "?"}
                </div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                  {s.team && <span>{s.team}</span>}
                  {s.language && (
                    <>
                      {s.team && <span aria-hidden>·</span>}
                      <span className="font-mono">{s.language}</span>
                    </>
                  )}
                  {s.type && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="uppercase tracking-wide">{s.type}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TimelineSection({ events }: { events: TimelineLike[] }) {
  return (
    <section>
      <SectionHeader title="Timeline" count={events.length} />
      <ol className="relative space-y-3 pl-4">
        <span
          aria-hidden
          className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-border"
        />
        {events.map((t, i) => {
          const tone = eventTone(t);
          return (
            <li key={i} className="relative grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3">
              <span
                aria-hidden
                className={cn(
                  "absolute -left-4 top-1.5 h-2.5 w-2.5 rounded-full border-2",
                  tone === "anomaly" && "bg-primary border-primary",
                  tone === "deploy" && "bg-amber-500 border-amber-500",
                  tone === "info" && "bg-card border-muted-foreground/40",
                )}
              />
              <span className="text-xs font-mono text-muted-foreground pt-0.5">
                {t.at ?? ""}
              </span>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">
                    {t.event ?? ""}
                  </span>
                  {t.service && (
                    <span className="text-[11px] text-muted-foreground font-mono">
                      @ {t.service}
                    </span>
                  )}
                </div>
                {t.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {count !== undefined && (
        <span className="text-[11px] text-muted-foreground font-mono">{count}</span>
      )}
    </div>
  );
}
